import { yupResolver } from "@hookform/resolvers/yup";
import { CheckCircledIcon, UploadIcon } from "@radix-ui/react-icons";
import { Button, Card, Flex, Heading, Text } from "@radix-ui/themes";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useQueryClient } from "react-query";
import { DragAndDropFileUpload } from "../../components/inputs/FileUpload/DragAndDropFileUpload";
import { postFormData } from "../../utils/axios-config";
import { Key_universities, UPLOAD_IMAGES } from "../../utils/constants";
import { UploadImagesFormValidation } from "./UploadImagesFormValidation";

export type FileType = "favicon" | "logo" | "background";

interface UploadItemType {
  type: FileType;
  file: File;
  preview?: string;
  uploaded?: boolean;
}

interface UploadItemProps {
  type: FileType;
  initialPreview?: string;
}

export const UploadItem = ({
  type = "logo",
  initialPreview,
}: UploadItemProps) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<UploadItemType>({
    resolver: yupResolver(UploadImagesFormValidation),
    defaultValues: { type },
    mode: "onChange",
  });

  const handleFileSelect = (file?: File) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setValue("preview", reader.result as string);
    reader.readAsDataURL(file);
  };

  const preview = watch("preview");

  useEffect(() => {
    if (!initialPreview) return;
    setValue("preview", initialPreview);
  }, [initialPreview, setValue]);
  const queryClient = useQueryClient();
  const onSubmit = handleSubmit(async (data) => {
    try {
      const formData = new FormData();
      formData.append("file", data.file);
      formData.append("fileType", data.type);
      await postFormData(UPLOAD_IMAGES, formData);
      setValue("uploaded", true);
      toast.success("Imagem carregada com sucesso");
      queryClient.invalidateQueries(Key_universities);
    } catch {
      toast.error("Erro ao carregar imagem");
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate>
      <Flex direction="column" gap="4">
        <Card style={{ padding: "20px", marginBottom: "10px" }}>
          <Flex direction="row" gap="4" align="start">
            <div
              style={{
                width: "150px",
                height: "150px",
                border: "5px dashed #ccc",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                backgroundColor: "#f9f9f9",
              }}
            >
              {preview ? (
                <img
                  src={preview}
                  alt="preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <UploadIcon width="40" height="40" color="#ccc" />
              )}
            </div>

            <Flex direction="column" gap="2" style={{ flex: 1 }}>
              <Heading
                size="4"
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#333",
                  textTransform: "capitalize",
                }}
              >
                {type}
              </Heading>

              <Flex gap="2" mt="2">
                <Controller
                  name="file"
                  control={control}
                  render={({ field }) => (
                    <DragAndDropFileUpload
                      onFileDrop={(file) => {
                        field.onChange(file);
                        handleFileSelect(file!);
                      }}
                      label="Arraste ou selecione o ficheiro"
                      selectedFile={field.value}
                    />
                  )}
                />
                {errors.file && (
                  <Text color="red" size="1">{`${errors.file?.message}`}</Text>
                )}
                <Button
                  type="submit"
                  disabled={watch("uploaded")}
                  color="green"
                >
                  {watch("uploaded") ? (
                    <>
                      <CheckCircledIcon /> Enviado
                    </>
                  ) : (
                    "Carregar Ficheiro"
                  )}
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Card>
      </Flex>
    </form>
  );
};
